import { useCallback, useEffect, useRef, useState } from 'react';

import senglarGif from '../../assets/images/senglar.gif';
import { axiosInstance } from '../../config/axios';
import { useAuth } from '../../hooks/useAuth';

import * as S from './FlyingSenglar.styles';

// ─── Design ──────────────────────────────────────────────────────────────────
//
// Phases: idle → flying (Lissajous flight over the live page, hover to
// charge the overlay) → landing (fly to start position + 3D flip) → game.
// Every phase runs in a single rAF loop driven by performance.now() deltas,
// so speed is identical on any machine / refresh rate.
//
// Fairness: the game runs on a viewport-INDEPENDENT coordinate system —
// vertical quantities are fractions of viewport height, and ALL constants
// are expressed per second in those units. Physics take identical time on
// any screen; a window resize remaps everything (canvas, pipes, bird)
// proportionally.
//
// Escape hatches: Esc exits any phase; an automated "fly away" cleans up
// the flying phase after FLY_AWAY_AFTER (also covering the never-hover
// case); the ✕ button exits the game.
//
// Triggers: typing "senglar" anywhere OUTSIDE text inputs/fields (guard
// against accidental triggers while writing), or rapid-tapping the nav
// logo on touch devices (6 taps within 2.5s — mobile has no hardware
// keyboard to type outside a field).
//
// Custom pipe images: fetched + preloaded once per session while the pig
// is still FLYING (network time hidden behind the flight); retries reuse
// them with zero loading. Input is never blocked on images: pipes spawn
// ~1.5s after the first jump, long after preload finished (or the images
// pop in seamlessly on the canvas via drawImage).
//
// The GIF was downscaled at source (480×550, ~37 KB) — frame decode cost
// is now ~1 MB instead of ~100 MB, which was the root cause of the
// stutter on Safari/low-end devices. The same asset doubles as the
// site-wide loading spinner, so everything benefits.

// ─── Types ───────────────────────────────────────────────────────────────────

interface Pipe {
	xFrac: number; // left edge ÷ viewport width
	topFrac: number; // top pipe height ÷ viewport height
	passed: boolean;
	imgTop: number; // index into pipe image array (-1 = no image)
	imgBottom: number;
}

type Phase = 'idle' | 'flying' | 'landing' | 'game';

// ─── Constants ───────────────────────────────────────────────────────────────

// Physics in fractions-of-viewport-height per second (reference 720 px →
// gravity 4, jump -800/720, pipes 420/720... identical timing everywhere).
const PHYS = {
	gravity: 4.4,
	jump: -880 / 720,
	maxVelocity: 1.6,
	pipeSpeed: 450 / 720,
	spawnInterval: 1.35, // BASE seconds between pipes (defines the pipe SPACING: pipeSpeed × this)
	pipeSpacing: (450 / 720) * 1.35, // constant DISTANCE between pipes (frac of H) — spawn clock advances in distance units so the spacing stays constant as the speed ramps
	gap: 236 / 720,
	pipeWidth: 180 / 720, // was 210/720 — user: a bit narrower
	// Pre-trim the sprite's pig occupied only ~45%/31% of its frame, so the old
	// 140/720 box SHOWED a ~63×44px pig. The trimmed sprite fills its frame
	// (94%/75%), so the box is shrunk to 58/720 → visible pig ≈ 55×44 px = the
	// same apparent size as the pre-trim game. The 17% hitbox inset is relative
	// to this box, so the approved core-vs-pig hug (core ≈ 0.88× pig height) is
	// preserved exactly.
	birdSize: 58 / 720,
	hitboxInset: 17 / 100, // default = fraction of the bird box removed per side (preset 2)
	edgeMargin: 80 / 720,
	minTopVariation: 0.16, // consecutive gap heights must differ by this much
	spawnDelay: 0.9, // seconds until the first pipe after the first jump
	rotFactor: 36, // deg per frac/s of velocity
	speedRampPerPoint: 0.015, // +1.5% pipe speed per scored pipe
	speedRampMax: 0.3,
};

const MAX_DT = 0.05;
const FLIGHT_SPEED = 0.8; // Lissajous clock advance per second (fast chase)
const FLY_AWAY_AFTER = 120; // seconds of autonomous flying before leaving
const FLY_AWAY_DURATION = 0.9;
const LANDING_DURATION = 1.4;
const FLIP_DURATION = 1.0;
const OVERLAY_HOVER_RATE = 0.85; // opacity per s while hovering (~1.2s to catch — was 0.75; the smaller post-trim pig needs the faster charge to stay catchable)
const OVERLAY_FADE_RATE = 2.2;
const HOVER_SPEEDUP = 1.25; // pig speeds up while you chase it (was 1.8 — too frantic at this size)
const DODGE_RADIUS = 170; // px: cursor closer than this = pig flees (was 200)
const DODGE_DISTANCE = 100; // how far the pig pushes itself away (was 150 — flees a shorter distance so it stays reachable without perfect cornering)
const MAX_PIPE_IMAGES = 25;
const LOGO_TAP_COUNT = 6;
const LOGO_TAP_WINDOW = 2.5;

// ─── Hitbox debug/visualizer (game only) ────────────────────────────────────
// URL ?senglar-debug=1 draws the live physics boxes; ?senglar-hitbox=N
// (0..3, or keys 0-3 while playing) picks an inset preset so the player
// can see and choose the real hitbox size.
const HITBOX_PRESETS = [
	{ label: '0% inset (whole sprite box)', inset: 0 },
	{ label: '8% inset', inset: 0.08 },
	{ label: '17% inset (moderate)', inset: 0.17 },
	{ label: '27% inset (current forgiving)', inset: 0.27 },
];
const senglarDebug =
	typeof window !== 'undefined' &&
	new URLSearchParams(window.location.search).has('senglar-debug');
// default preset = 2 (17% —— hugs the visible pig; 27% too forgiving after the
// trim); ?senglar-hitbox=N overrides; keys 0-3 switch live in debug mode.
const initialHitboxIdx = (() => {
	const v = new URLSearchParams(window.location.search).get('senglar-hitbox');
	const n = v == null ? NaN : parseInt(v, 10);
	let idx = 2;
	if (!isNaN(n)) idx = Math.max(0, Math.min(HITBOX_PRESETS.length - 1, n));
	return idx;
})();
const hitboxInsetRef = { current: HITBOX_PRESETS[initialHitboxIdx].inset };

// birdX (px): fixed-ish share of width, clamped so it scales down on phones.
const birdXOf = (W: number): number => Math.max(100, Math.min(W * 0.25, 320));
const FLY_SIZE_FRAC = 7.65 / 8.06; // renders ~7.65vh box → visible pig ≈ 7.2vh, the pre-trim look

const lissX = (t: number, W: number): number => {
	const raw = Math.sin(t) + 0.5 * Math.cos(t * 2.3);
	return Math.floor(W / 2 + (raw / 1.5) * (W / 2 - 50));
};
const lissY = (t: number, H: number): number => {
	const raw = Math.cos(t) + 0.5 * Math.sin(t * 2.1);
	return Math.floor(H / 2 + (raw / 1.5) * (H / 2 - 50));
};

const EDITABLE_SELECTOR =
	'input, textarea, select, [contenteditable="true"], [contenteditable=""]';

// ─── Game ────────────────────────────────────────────────────────────────────

interface FlappyBirdGameProps {
	pipeImgs: HTMLImageElement[] | null; // null = no custom images
	onExit: () => void;
}

const FlappyBirdGame: React.FC<FlappyBirdGameProps> = ({
	pipeImgs,
	onExit,
}) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const birdImgRef = useRef<HTMLImageElement>(null);

	const [score, setScore] = useState(0);
	const [gameOver, setGameOver] = useState(false);
	const [started, setStarted] = useState(false);

	const stateRef = useRef({
		birdY: 0.5 - PHYS.birdSize / 2, // frac of H
		velocity: 0, // frac/s
		pipes: [] as Pipe[],
		nextImg: 0,
		score: 0,
		gameOver: false,
		started: false,
		rotation: 0,
	});
	const rafRef = useRef(0);
	const lastTimeRef = useRef(0);
	const pipeTimerRef = useRef(0);

	const draw = useCallback(
		(ctx: CanvasRenderingContext2D, W: number, H: number) => {
			ctx.fillStyle = '#3498db';
			ctx.fillRect(0, 0, W, H);
			const s = stateRef.current;
			const pw = PHYS.pipeWidth * H;
			const gap = PHYS.gap * H;
			ctx.fillStyle = '#27ae60';
			for (const p of s.pipes) {
				const x = p.xFrac * W;
				const top = p.topFrac * H;
				ctx.fillRect(x, 0, pw, top);
				ctx.fillRect(x, top + gap, pw, H - top - gap);
			}
			// Custom pipe images drawn on the SAME canvas — single render path.
			if (pipeImgs) {
				for (const p of s.pipes) {
					const x = p.xFrac * W;
					const top = p.topFrac * H;
					const it = pipeImgs[p.imgTop];
					const ib = pipeImgs[p.imgBottom];
					if (it && it.complete && it.naturalWidth > 0) {
						const h = (it.naturalHeight / it.naturalWidth) * pw;
						ctx.drawImage(it, x, top - h, pw, h);
					}
					if (ib && ib.complete && ib.naturalWidth > 0) {
						const h = (ib.naturalHeight / ib.naturalWidth) * pw;
						ctx.drawImage(ib, x, top + gap, pw, h);
					}
				}
			}

			// Pipe outline: dark border so the pipes read clearly against the
			// sky (green #27ae60 on blue #3498db is too low-contrast otherwise).
			// Outlined = whatever was actually drawn for that side: the custom
			// image bounds when an image is loaded, else the plain rect.
			ctx.strokeStyle = 'rgba(23,44,32,0.9)';
			ctx.lineWidth = Math.max(2, PHYS.pipeWidth * H * 0.02);
			for (const p of s.pipes) {
				const x = p.xFrac * W;
				const top = p.topFrac * H;
				const it = pipeImgs?.[p.imgTop];
				const ib = pipeImgs?.[p.imgBottom];
				if (it && it.complete && it.naturalWidth > 0) {
					const h = (it.naturalHeight / it.naturalWidth) * pw;
					ctx.strokeRect(x, top - h, pw, h);
				} else {
					ctx.strokeRect(x, 0, pw, top);
				}
				if (ib && ib.complete && ib.naturalWidth > 0) {
					const h = (ib.naturalHeight / ib.naturalWidth) * pw;
					ctx.strokeRect(x, top + gap, pw, h);
				} else {
					ctx.strokeRect(x, top + gap, pw, H - top - gap);
				}
			}

		if (senglarDebug) {
			// live physics visualization: full physics box (yellow), the
			// collision CORE (pink), each gap column (pink). Lives in draw()
			// so it also renders on idle + game-over frames.
			const box = PHYS.birdSize * H;
			const inset = hitboxInsetRef.current * box;
			const bx = birdXOf(W);
			ctx.lineWidth = 2;
			ctx.strokeStyle = '#ffe600';
			ctx.strokeRect(bx, s.birdY * H, box, box);
			ctx.strokeStyle = '#ff2bd6';
			ctx.strokeRect(bx + inset, s.birdY * H + inset, box - 2 * inset, box - 2 * inset);
			for (const p of s.pipes) {
				ctx.strokeStyle = '#ff2bd6';
				ctx.strokeRect(p.xFrac * W, p.topFrac * H, PHYS.pipeWidth * H, PHYS.gap * H);
			}
			ctx.fillStyle = 'rgba(255,255,255,0.92)';
			ctx.fillRect(12, H - 42, 470, 28);
			ctx.fillStyle = '#111';
			ctx.font = '15px Trebuchet MS, sans-serif';
			ctx.fillText(`hitbox: ${(hitboxInsetRef.current * 100).toFixed(0)}% inset per costat — prem 0-3`, 20, H - 22);
		}
		},
		[pipeImgs]
	);

	// Game loop: single rAF, dt-based. Pre-start = cheap idle, no work.
	useEffect(() => {
		lastTimeRef.current = performance.now();
		pipeTimerRef.current = -PHYS.spawnDelay;

		const loop = (now: number) => {
			const s = stateRef.current;
			const W = window.innerWidth;
			const H = window.innerHeight;
			const ctx = canvasRef.current?.getContext('2d');

			if (!s.started) {
				rafRef.current = requestAnimationFrame(loop);
				if (ctx) draw(ctx, W, H);
				return;
			}

			const dt = Math.min((now - lastTimeRef.current) / 1000, MAX_DT);
			lastTimeRef.current = now;

			if (!ctx || s.gameOver) {
				// Keep the loop alive so a later Reintentar can resume it;
				// just don't advance physics past the final frozen frame.
				if (ctx) draw(ctx, W, H);
				rafRef.current = requestAnimationFrame(loop);
				return;
			}

			// Physics (fraction units).
			s.velocity = Math.max(
				Math.min(s.velocity + PHYS.gravity * dt, PHYS.maxVelocity),
				-PHYS.maxVelocity
			);
			s.birdY += s.velocity * dt;
			s.rotation = Math.max(-30, Math.min(30, s.velocity * PHYS.rotFactor));

			if (s.birdY < 0 || s.birdY + PHYS.birdSize > 1) {
				// NO return/draw here — falling through keeps the rAF loop alive
				// (the gameOver branch above keeps redrawing the frozen frame),
				// otherwise Reintentar would have nothing to resume.
				s.gameOver = true;
				setGameOver(true);
			}

			// Horizontal scroll in frac/W so px/s stays H-scaled; pipes die off-screen.
			const speedMul = 1 + Math.min(PHYS.speedRampPerPoint * s.score, PHYS.speedRampMax);
			s.pipes = s.pipes
				.map((p) => ({ ...p, xFrac: p.xFrac - (PHYS.pipeSpeed * speedMul * dt * H) / W }))
				.filter((p) => p.xFrac * W + PHYS.pipeWidth * H > -12);

			// Spawning: fixed DISTANCE between pipes, not fixed time — otherwise
			// the speed ramp stretches the spacing (speed × interval) and the
			// game gets EASIER as it gets faster.
			pipeTimerRef.current += PHYS.pipeSpeed * speedMul * dt;
			const minTop = PHYS.edgeMargin;
			const maxTop = 1 - PHYS.gap - PHYS.edgeMargin;
			if (pipeTimerRef.current >= PHYS.pipeSpacing) {
				pipeTimerRef.current = 0;
				const pick = () =>
					pipeImgs && pipeImgs.length > 0
						? s.nextImg++ % pipeImgs.length
						: -1;
				// Force visible gap-height variation between consecutive pipes.
				const lastTop = s.pipes.length
					? s.pipes[s.pipes.length - 1].topFrac
					: -1;
				let topFrac = minTop + Math.random() * (maxTop - minTop);
				for (let tries = 0; tries < 5 && Math.abs(topFrac - lastTop) < PHYS.minTopVariation; tries++) {
					topFrac = minTop + Math.random() * (maxTop - minTop);
				}
				if (Math.abs(topFrac - lastTop) < PHYS.minTopVariation) {
					// fall back: jump the other way from the last gap
					topFrac = Math.max(minTop, Math.min(maxTop, lastTop - PHYS.minTopVariation * 1.5));
				}
				s.pipes.push({
					xFrac: 1 + 12 / W,
					topFrac,
					passed: false,
					imgTop: pick(),
					imgBottom: pick(),
				});
			}

			// Scoring.
			const birdX = birdXOf(W);
			const pipeW = PHYS.pipeWidth * H;
			let newScore = s.score;
			for (const p of s.pipes) {
				if (!p.passed && p.xFrac * W + pipeW < birdX) {
					newScore += 1;
					p.passed = true;
				}
			}
			if (newScore !== s.score) {
				s.score = newScore;
				setScore(newScore);
			}

			// Collisions (bird box shrunk by the hitbox inset on every side).
			const inset = hitboxInsetRef.current * PHYS.birdSize * H;
			const hitL = birdX + inset;
			const hitR = birdX + PHYS.birdSize * H - inset;
			const hitT = s.birdY * H + inset;
			const hitB = (s.birdY + PHYS.birdSize) * H - inset;
			const gap = PHYS.gap * H;
			for (const p of s.pipes) {
				const x = p.xFrac * W;
				if (hitR > x && hitL < x + pipeW) {
					if (p.topFrac * H > hitT || hitB > p.topFrac * H + gap) {
						// fall through (no return!) — keeps the loop alive for Reintentar
						s.gameOver = true;
						setGameOver(true);
					}
				}
			}

			// Bird sprite (translate + rotate + horizontal flip in one transform).
			birdImgRef.current?.style.setProperty(
				'transform',
				`translate(${birdX}px, ${s.birdY * H}px) rotate(${s.rotation}deg) scaleX(-1)`
			);

			draw(ctx, W, H);
			rafRef.current = requestAnimationFrame(loop);
		};

		const onDebugKey = (e: KeyboardEvent) => {
			if (!senglarDebug) return;
			const n = parseInt(e.key, 10);
			if (!isNaN(n) && n >= 0 && n < HITBOX_PRESETS.length) {
				hitboxInsetRef.current = HITBOX_PRESETS[n].inset;
			}
		};
		if (senglarDebug) window.addEventListener('keydown', onDebugKey);
		const cleanupDebug = () => {
			if (senglarDebug) window.removeEventListener('keydown', onDebugKey);
		};

		rafRef.current = requestAnimationFrame(loop);
		return () => {
			cleanupDebug();
			cancelAnimationFrame(rafRef.current);
		};
	}, [draw]);

	// Canvas sizing + resize handling: remap running state proportionally.
	useEffect(() => {
		const sizeCanvas = () => {
			const canvas = canvasRef.current;
			if (!canvas) return;
			const dpr = Math.min(window.devicePixelRatio || 1, 2);
			const W = window.innerWidth;
			const H = window.innerHeight;
			canvas.width = Math.round(W * dpr);
			canvas.height = Math.round(H * dpr);
			const ctx = canvas.getContext('2d');
			if (ctx) {
				ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
				draw(ctx, W, H);
			}
		};
		sizeCanvas();
		window.addEventListener('resize', sizeCanvas);
		return () => window.removeEventListener('resize', sizeCanvas);
	}, [draw]);

	// Retry: reset everything local; images (already preloaded) are reused.
	const retry = useCallback(() => {
		const s = stateRef.current;
		s.birdY = 0.5 - PHYS.birdSize / 2;
		s.velocity = 0;
		s.pipes = [];
		s.nextImg = 0;
		s.score = 0;
		s.gameOver = false;
		s.started = false;
		s.rotation = 0;
		setScore(0);
		setGameOver(false);
		setStarted(false);
		pipeTimerRef.current = -PHYS.spawnDelay;
		lastTimeRef.current = performance.now();
	}, []);

	// Place the bird at its starting spot on mount and after retry
	// (not on death — the corpse stays where it fell).
	useEffect(() => {
		if (gameOver) return;
		const place = () => {
			const W = window.innerWidth;
			const H = window.innerHeight;
			birdImgRef.current?.style.setProperty(
				'transform',
				`translate(${birdXOf(W)}px, ${(0.5 - PHYS.birdSize / 2) * H}px) scaleX(-1)`
			);
		};
		place();
	}, [started, gameOver]);

	// Input: pointerdown (mouse + touch unified), Space; both prevented from
	// interacting with the page behind. Only active once mounted (= ready).
	useEffect(() => {
		const jump = () => {
			const s = stateRef.current;
			if (s.gameOver) return;
			if (!s.started) {
				s.started = true;
				setStarted(true);
			}
			s.velocity = PHYS.jump;
		};
		const onPointer = (e: PointerEvent) => {
			// Let the Game Over / exit buttons handle their own clicks.
			if (e.target instanceof Element && e.target.closest('button')) return;
			jump();
			e.preventDefault();
		};
		const onKey = (e: KeyboardEvent) => {
			if (e.key !== ' ') return;
			e.preventDefault(); // stop the page scrolling behind the game
			jump();
		};
		window.addEventListener('pointerdown', onPointer, { passive: false });
		window.addEventListener('keydown', onKey);
		return () => {
			window.removeEventListener('pointerdown', onPointer);
			window.removeEventListener('keydown', onKey);
		};
	}, []);

	return (
		<>
			<S.Canvas ref={canvasRef} />
			<S.BirdWrapper>
				<S.BirdImg
					ref={birdImgRef}
					src={senglarGif}
					alt="Senglar"
					draggable={false}
					style={{ transform: 'scaleX(-1)' }}
				/>
			</S.BirdWrapper>
			<S.ScoreDisplay>{score}</S.ScoreDisplay>
			<S.CloseButton onClick={onExit} aria-label="Sortir del joc" title="Sortir (Esc)">
				&times;
			</S.CloseButton>
			{gameOver && (
				<S.GameOverPanel>
					<S.GameOverTitle>Game Over</S.GameOverTitle>
					<S.GameOverScore>
						Puntuació: <S.GameOverScoreValue>{score}</S.GameOverScoreValue>
					</S.GameOverScore>
					<S.GameOverButtons>
						<S.RetryButton onClick={retry}>Reintentar</S.RetryButton>
						<S.ExitButton onClick={onExit}>Sortir</S.ExitButton>
					</S.GameOverButtons>
				</S.GameOverPanel>
			)}
			{!started && !gameOver && (
				<S.StartPrompt>Clic o toc per començar</S.StartPrompt>
			)}
		</>
	);
};

// ─── Main component ──────────────────────────────────────────────────────────

const FlyingSenglar = () => {
	const { user } = useAuth();
	const [phase, setPhase] = useState<Phase>('idle');
	const [overlayOpacity, setOverlayOpacity] = useState(0);
	const overlayRef = useRef(0);
	const [bufferedKeys, setBufferedKeys] = useState('');

	// Custom pipe images: fetched once per session, reused across everything.
	const canSeeCustomImages = user?.roles.some((r) =>
		['Admin', 'Agrupament', 'Cap'].includes(r)
	);
	const [pipeImgs, setPipeImgs] = useState<HTMLImageElement[] | null>(null);
	const pipeImgsFetchedRef = useRef(false);

	const timeRef = useRef(0); // Lissajous clock (seconds)
	const flightTimeRef = useRef(0);
	const landingStartRef = useRef({ x: 0, y: 0 });
	const rafRef = useRef(0);
	const senglarRef = useRef<HTMLImageElement>(null);
	const logoTapsRef = useRef({ count: 0, windowStart: 0 });
	const dodgeXRef = useRef(0); // cursor-driven evasion offset (flying)
	const dodgeYRef = useRef(0);

	// ── Triggers (only while idle) ─────────────────────────────────────────────

	useEffect(() => {
		if (phase !== 'idle') return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.isComposing || event.key.length !== 1) return;
			if (event.target instanceof Element && event.target.closest(EDITABLE_SELECTOR)) {
				return; // never hijack typing in fields
			}
			setBufferedKeys((prev) => (prev + event.key).slice(-7));
		};

		const onClick = (event: MouseEvent) => {
			const target = event.target instanceof Element ? event.target : null;
			if (!target || !target.closest<HTMLElement>('img[alt="AEIG Sant Narcís"]')) {
				return;
			}
			const taps = logoTapsRef.current;
			const now = performance.now();
			if (now - taps.windowStart > LOGO_TAP_WINDOW * 1000) taps.count = 0;
			taps.windowStart = now;
			taps.count += 1;
			if (taps.count >= LOGO_TAP_COUNT) {
				taps.count = 0;
				setPhase('flying');
			}
		};

		window.addEventListener('keydown', onKeyDown);
		window.addEventListener('click', onClick);
		return () => {
			window.removeEventListener('keydown', onKeyDown);
			window.removeEventListener('click', onClick);
		};
	}, [phase]);

	// "senglar" completed → start (only from idle).
	useEffect(() => {
		if (phase === 'idle' && bufferedKeys.endsWith('senglar')) {
			setBufferedKeys('');
			setPhase('flying');
		}
	}, [bufferedKeys, phase]);

	// ── Flying phase ───────────────────────────────────────────────────────────
	//
	// Catch mechanic (proximity-driven, no DOM hover events — those caused
	// an oscillation: dodge → hover lost → dodge decay → hover again →
	// repeated flicker). The pig ALWAYS keeps a safe distance from a
	// mouse cursor; the player must corner it against a screen edge and
	// keep the cursor on it for ~1.3s. Touch devices: press-and-hold on
	// the pig (no dodge for touch, or it would be uncapturable).

	useEffect(() => {
		if (phase !== 'flying') return;

		// Preload custom images NOW (hidden behind the flight time).
		if (canSeeCustomImages && !pipeImgsFetchedRef.current) {
			pipeImgsFetchedRef.current = true;
			axiosInstance
				.get<string[]>('/api/flappy-images/game')
				.then((res) => {
					const shuffled = [...res.data].sort(() => Math.random() - 0.5);
					const els = shuffled.slice(0, MAX_PIPE_IMAGES).map((url) => {
						const el = new Image();
						el.src = url;
						return el;
					});
					setPipeImgs(els.length > 0 ? els : null);
				})
				.catch(() => {
					/* silently fall back to plain pipes */
					setPipeImgs(null);
				});
		}

		let lastTime = performance.now();
		let flyAwayStart: number | null = null;
		let flyAwayX = 0;
		let flyAwayY = 0;
		flightTimeRef.current = 0; // fresh 2-minute budget every entry

		// Pointer state: position + whether it's a mouse (dodges) or a
		// touch/pen (press-and-hold catch, no dodge).
		const pointer = { x: -99999, y: -99999, kind: 'none', pressed: false };
		const onMove = (e: PointerEvent) => {
			pointer.kind = e.pointerType;
			pointer.x = e.clientX;
			pointer.y = e.clientY;
			pointer.pressed = e.pointerType !== 'mouse';
		};
		const onDown = (e: PointerEvent) => {
			pointer.kind = e.pointerType;
			pointer.x = e.clientX;
			pointer.y = e.clientY;
			pointer.pressed = true;
		};
		const onUp = (e: PointerEvent) => {
			if (e.pointerType !== 'mouse') {
				pointer.kind = 'touch';
				pointer.pressed = false;
			}
		};
		window.addEventListener('pointermove', onMove);
		window.addEventListener('pointerdown', onDown);
		window.addEventListener('pointerup', onUp);
		window.addEventListener('pointercancel', onUp);

		const animate = (now: number) => {
			const dt = Math.min((now - lastTime) / 1000, MAX_DT);
			lastTime = now;
			const W = window.innerWidth;
			const H = window.innerHeight;
			const pigW = 0.0765 * H; // flying sprite box (8.06vh wrapper × 0.949 flight scale ≈ 7.65vh) — kept in sync with FLY_SIZE_FRAC, was 0.16*H pre-trim
			const lissPx = lissX(timeRef.current, W);
			const lissPy = lissY(timeRef.current, H);

			// Current pig position incl. dodge, and cursor-on-pig test.
			const isMouse = pointer.kind === 'mouse';
			const pigX = lissPx + dodgeXRef.current;
			const pigY = lissPy + dodgeYRef.current;
			const pad = 36; // generous: the pig is small post-trim — 36px ≈ half its size
			const onPig =
				pointer.x > pigX - pad &&
				pointer.x < pigX + pigW + pad &&
				pointer.y > pigY - pad &&
				pointer.y < pigY + pigW + pad &&
				(isMouse ? true : pointer.pressed);

			// Automated fly-away after the timeout, from wherever it flies.
			flightTimeRef.current += dt;
			if (flyAwayStart === null && flightTimeRef.current >= FLY_AWAY_AFTER) {
				flyAwayStart = now;
				flyAwayX = pigX;
				flyAwayY = pigY;
			}
			if (flyAwayStart !== null) {
				const k = Math.min((now - flyAwayStart) / 1000 / FLY_AWAY_DURATION, 1);
				const easeX = k * k;
				senglarRef.current?.style.setProperty(
					'transform',
					`translate(${flyAwayX + 2400 * easeX}px, ${flyAwayY - 500 * easeX}px) scale(${FLY_SIZE_FRAC})`
				);
				if (k >= 1) {
					overlayRef.current = 0;
					setOverlayOpacity(0);
					setPhase('idle');
					return;
				}
				rafRef.current = requestAnimationFrame(animate);
				return;
			}

			// Dodge: mice only. Maintain a SAFE center-distance from the
			// cursor (converges — no oscillation); corner it to catch it.
			if (isMouse) {
				const dx = pigX + pigW / 2 - pointer.x;
				const dy = pigY + pigW / 2 - pointer.y;
				const dist = Math.hypot(dx, dy) || 1;
				if (dist < DODGE_RADIUS) {
					const cx = pointer.x + (dx / dist) * DODGE_DISTANCE - pigW / 2;
					const cy = pointer.y + (dy / dist) * DODGE_DISTANCE - pigW / 2;
					// retreat toward the lissajous path as well, so it keeps
					// moving rather than becoming static while cornered
					const mixT = 0.4;
					let targetX = lissPx * (1 - mixT) + cx * mixT;
					let targetY = lissPy * (1 - mixT) + cy * mixT;
					targetX = Math.max(10, Math.min(W - pigW - 10, targetX));
					targetY = Math.max(60, Math.min(H - pigW - 10, targetY));
					const k = Math.min(1, 6 * dt);
					dodgeXRef.current += (targetX - lissPx - dodgeXRef.current) * k;
					dodgeYRef.current += (targetY - lissPy - dodgeYRef.current) * k;
				} else {
					// out of range: glide back onto the flight path
					const k = Math.min(1, 4 * dt);
					dodgeXRef.current += (0 - dodgeXRef.current) * k;
					dodgeYRef.current += (0 - dodgeYRef.current) * k;
				}
			} else {
				// touch/pen: dodge decays to zero
				const k = Math.min(1, 6 * dt);
				dodgeXRef.current += (0 - dodgeXRef.current) * k;
				dodgeYRef.current += (0 - dodgeYRef.current) * k;
			}

			// The pig speeds up (and stays lively) whenever the cursor is near.
			const chase =
				isMouse && onPig
					? HOVER_SPEEDUP
					: (() => {
							const dx = lissPx + dodgeXRef.current + pigW / 2 - pointer.x;
							const dy = lissPy + dodgeYRef.current + pigW / 2 - pointer.y;
							return Math.hypot(dx, dy) < DODGE_RADIUS ? 1.1 : 1;
					  })();
			timeRef.current += FLIGHT_SPEED * chase * dt;

			// Charge only while the cursor is ACTUALLY on the pig.
			const rate = onPig ? OVERLAY_HOVER_RATE : -OVERLAY_FADE_RATE;
			const next = Math.max(0, Math.min(1, overlayRef.current + rate * dt));
			overlayRef.current = next;
			setOverlayOpacity(next);
			if (next >= 1) {
				landingStartRef.current = {
					x: lissPx + dodgeXRef.current,
					y: lissPy + dodgeYRef.current,
				};
				setPhase('landing');
				return;
			}

			senglarRef.current?.style.setProperty(
				'transform',
				`translate(${lissPx + dodgeXRef.current}px, ${lissPy + dodgeYRef.current}px) scale(${FLY_SIZE_FRAC})`
			);

			rafRef.current = requestAnimationFrame(animate);
		};

		rafRef.current = requestAnimationFrame(animate);
		return () => {
			cancelAnimationFrame(rafRef.current);
			window.removeEventListener('pointermove', onMove);
			window.removeEventListener('pointerdown', onDown);
			window.removeEventListener('pointerup', onUp);
			window.removeEventListener('pointercancel', onUp);
		};
	}, [phase, canSeeCustomImages]);

	// ── Landing phase ──────────────────────────────────────────────────────────

	useEffect(() => {
		if (phase !== 'landing') return;

		const W = window.innerWidth;
		const H = window.innerHeight;
		const targetX = birdXOf(W);
		const targetY = H / 2 - (PHYS.birdSize * H) / 2;

		// The pig flies in small (16vh) and grows to game size on arrival.
		const fromScale = FLY_SIZE_FRAC;
		const arriveT = LANDING_DURATION; // scale interpolates over the fly-in

		let lastTime = performance.now();
		let elapsed = 0;
		let inFlipMode = false;

		const animate = (now: number) => {
			const dt = Math.min((now - lastTime) / 1000, MAX_DT);
			lastTime = now;
			elapsed += dt;

			const t = Math.min(elapsed / LANDING_DURATION, 1);
			// ease-in-out so the pig accelerates in and settles on its spot
			const ease = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
			const x =
				landingStartRef.current.x +
				(targetX - landingStartRef.current.x) * ease;
			const y =
				landingStartRef.current.y +
				(targetY - landingStartRef.current.y) * ease;

			const flipProgress =
				t >= 1 ? Math.min((elapsed - LANDING_DURATION) / FLIP_DURATION, 1) : 0;

			if (senglarRef.current) {
				const scale =
					fromScale +
					(1 - fromScale) * Math.min(t / arriveT, 1) ** 0.7;
				senglarRef.current.style.setProperty(
					'transform',
					`translate(${x}px, ${y}px) scale(${scale})`
				);
				if (flipProgress === 0) {
					if (inFlipMode) {
						senglarRef.current.style.perspective = '';
						senglarRef.current.style.transformStyle = 'flat';
						inFlipMode = false;
					}
				} else {
					if (!inFlipMode) {
						senglarRef.current.style.perspective = '300px';
						senglarRef.current.style.transformStyle = 'preserve-3d';
						inFlipMode = true;
					}
					senglarRef.current.style.transform = `translate(${x}px, ${y}px) scale(${(1)}) rotateY(${flipProgress * 180}deg)`;
				}
			}

			if (elapsed < LANDING_DURATION + FLIP_DURATION) {
				rafRef.current = requestAnimationFrame(animate);
			} else {
				setPhase('game');
			}
		};

		rafRef.current = requestAnimationFrame(animate);
		return () => cancelAnimationFrame(rafRef.current);
	}, [phase]);

	// ── Exit (button or Escape) ────────────────────────────────────────────────

	const handleExit = useCallback(() => {
		cancelAnimationFrame(rafRef.current);
		setPhase('idle');
		overlayRef.current = 0;
		dodgeXRef.current = 0;
		dodgeYRef.current = 0;
		setOverlayOpacity(0);
		setBufferedKeys('');
		logoTapsRef.current = { count: 0, windowStart: 0 };
	}, []);

	useEffect(() => {
		if (phase === 'idle') return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') handleExit();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [phase, handleExit]);

	// ── Render ─────────────────────────────────────────────────────────────────

	if (phase === 'idle') return <></>;

	return (
		<S.Root $blockPointer={phase === 'game' || phase === 'landing'}>
			<S.Overlay $op={overlayOpacity} />
			{phase === 'game' ? (
				<FlappyBirdGame pipeImgs={pipeImgs} onExit={handleExit} />
			) : (
				<S.BirdWrapper>
					<S.BirdImg
						ref={senglarRef}
						src={senglarGif}
						alt="Senglar volador"
						draggable={false}
					/>
				</S.BirdWrapper>
			)}
		</S.Root>
	);
};

export default FlyingSenglar;